import { Link, createFileRoute } from '@tanstack/react-router';
import { SITE_NAME, SITE_URL, buildHead } from '~/seo/site';
import { Button } from '~/ui/components/button';

// Root Organization + WebSite JSON-LD. Two things this buys us:
//   - Brand disambiguation: when someone googles "prodbattle" Google
//     can attach this entity to the SERP instead of merging us with
//     probattle.live / prodbatts.com / "Produce the Beat Battles".
//   - sitelinks searchbox eligibility via WebSite.potentialAction.
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/icon-512.png`,
  description:
    'Real-time online beat battle platform where producers compete head-to-head on the same sample kit.',
  sameAs: [
    'https://www.youtube.com/@prodbattle',
    'https://x.com/prodbattle',
    'https://www.instagram.com/prodbattle',
    'https://www.tiktok.com/@prodbattle',
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/feed?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export const Route = createFileRoute('/')({
  head: () => {
    const head = buildHead({
      title: 'Prod Battle - Online Beat Battles for Music Producers',
      description:
        'Online beat battle platform for music producers. Same sample kit for everyone in a match - skill, not gear. Quickplay, ranked, private rooms, weekly tournaments. Free to play.',
      path: '/',
    });
    return {
      ...head,
      meta: [
        ...head.meta,
        { 'script:ld+json': organizationSchema },
        { 'script:ld+json': websiteSchema },
      ],
    };
  },
  component: Home,
});

function Home() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-20 text-center space-y-6">
      <h1 className="text-5xl font-bold tracking-tight">
        A <span className="text-primary">game</span> for producers.
      </h1>
      <p className="text-lg text-muted-foreground">
        A real-time beat battle where the same sample kit drops to every producer at once - skill,
        not gear. Quickplay or ranked FFA up to 8, private rooms 1v1 to 5v5, Sample Flip remixes, or
        the Daily Challenge.
      </p>
      <p className="text-sm text-muted-foreground/80">
        Blind voting, ELO-tracked ranks, anti-smurf checks, and genre rules tuned by the community.
      </p>
      <p className="text-sm text-muted-foreground/80">
        Every submission is kept on your public profile as a track listing, and your wins move you
        up the leaderboard for that genre - one ranking per genre, so a phonk producer competes
        against phonk producers, not the whole site.
      </p>
      <p className="text-sm text-muted-foreground/80">
        Climb weekly showcases and bracket tournaments, take label briefs, add friends, and get
        scouted - A&R pick standout tracks and reach out to producers they're watching.
      </p>
      <div className="flex justify-center gap-4 pt-4">
        <Button asChild size="lg">
          <Link to="/play">Play now</Link>
        </Button>
        <Button asChild size="lg" variant="outline">
          <Link to="/feed">Just listen</Link>
        </Button>
      </div>
    </section>
  );
}
